import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
	Plus,
	Search,
	Filter,
	Bell,
	Trash2,
	Eye,
	AlertCircle,
	CheckCircle,
	Info,
	AlertTriangle,
	CheckCircle2,
} from "lucide-react";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { notificationsAPI, usersAPI, getApiErrorPayload, extractValidationErrors } from "@/lib/api";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { User } from "@/types";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/utils";

interface BackendNotification {
	id: string;
	title: string;
	message: string;
	type: "INFO" | "SUCCESS" | "WARNING" | "ERROR" | "SYSTEM" | "BUSINESS";
	status: "UNREAD" | "READ" | "ARCHIVED";
	priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
	createdAt: string;
	readAt?: string;
	createdBy?: {
		id: string;
		firstName?: string;
		lastName?: string;
		fullName?: string;
	};
	metadata?: any;
}

interface Notification {
	id: string;
	title: string;
	message: string;
	type: "info" | "success" | "warning" | "error";
	status: "unread" | "read" | "archived";
	priority: "low" | "medium" | "high";
	recipient: string;
	sentAt: string;
	readAt?: string;
}

// Transform backend notification to frontend format
const transformNotification = (
	backendNotif: BackendNotification,
	t: TFunction,
	currentUser?: User | null,
): Notification => {
	// Map enum values from uppercase to lowercase
	const typeMap: Record<string, "info" | "success" | "warning" | "error"> = {
		INFO: "info",
		SUCCESS: "success",
		WARNING: "warning",
		ERROR: "error",
		SYSTEM: "info",
		BUSINESS: "info",
	};

	const statusMap: Record<string, "unread" | "read" | "archived"> = {
		UNREAD: "unread",
		READ: "read",
		ARCHIVED: "archived",
	};

	const priorityMap: Record<string, "low" | "medium" | "high"> = {
		LOW: "low",
		MEDIUM: "medium",
		HIGH: "high",
		URGENT: "high",
	};

	// Determine recipient - use createdBy name if available, otherwise use metadata or default
	let recipient = t("notifications.type.system");
	if (backendNotif.createdBy?.firstName && backendNotif.createdBy?.lastName) {
		recipient = `${backendNotif.createdBy.firstName} ${backendNotif.createdBy.lastName}`;
	} else if (backendNotif.metadata?.recipient) {
		recipient = backendNotif.metadata.recipient;
	} else if (backendNotif.type === "SYSTEM") {
		recipient = t("notifications.recipients.allUsers");
	}

	return {
		id: backendNotif.id,
		title: backendNotif.title,
		message: backendNotif.message || "",
		type: typeMap[backendNotif.type] || "info",
		status: statusMap[backendNotif.status] || "unread",
		priority: priorityMap[backendNotif.priority] || "medium",
		recipient,
		sentAt: backendNotif.createdAt,
		readAt: backendNotif.readAt,
	};
};

const NotificationsPage: React.FC = () => {
	const navigate = useNavigate();
	const { isRTL } = useLanguage();
	const { toast } = useToast();
	const { t } = useTranslation();
	const { user } = useAuth();

	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Pagination and filters
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalNotifications, setTotalNotifications] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedType, setSelectedType] = useState<string>("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [selectedPriority, setSelectedPriority] = useState<string>("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");

	// New Notification Dialog
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [users, setUsers] = useState<User[]>([]);
	const [formData, setFormData] = useState({
		title: "",
		message: "",
		type: "INFO" as "INFO" | "SUCCESS" | "WARNING" | "ERROR" | "SYSTEM" | "BUSINESS",
		priority: "MEDIUM" as "LOW" | "MEDIUM" | "HIGH" | "URGENT",
		userId: "",
		actionUrl: "",
		actionText: "",
		expiresAt: "",
	});
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});
	const [itemsPerPage, setItemsPerPage] = useState(10);

	const fetchNotifications = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);

			// Build filters object
			const filters: any = {};
			if (selectedType) {
				// Map frontend lowercase to backend uppercase
				const typeMap: Record<string, string> = {
					info: "INFO",
					success: "SUCCESS",
					warning: "WARNING",
					error: "ERROR",
				};
				filters.type = typeMap[selectedType] || selectedType.toUpperCase();
			}
			if (selectedStatus) {
				filters.status = selectedStatus.toUpperCase();
			}
			if (selectedPriority) {
				filters.priority = selectedPriority.toUpperCase();
			}
			if (dateFrom) {
				filters.dateFrom = new Date(dateFrom).toISOString();
			}
			if (dateTo) {
				filters.dateTo = new Date(dateTo).toISOString();
			}

			// Fetch from API
			const response = await notificationsAPI.getAll(currentPage, itemsPerPage, filters);

			// Transform backend notifications to frontend format
			const transformedNotifications = response.data.map((notif: BackendNotification) =>
				transformNotification(notif, t, user),
			);

			// Apply frontend-side filtering for search (backend handles date filtering)
			let filteredNotifications = transformedNotifications;

			// Search filter (client-side for better UX)
			if (searchTerm) {
				const searchLower = searchTerm.toLowerCase();
				filteredNotifications = filteredNotifications.filter(
					(notif: Notification) =>
						notif.title.toLowerCase().includes(searchLower) ||
						notif.message.toLowerCase().includes(searchLower) ||
						notif.recipient.toLowerCase().includes(searchLower),
				);
			}

			setNotifications(filteredNotifications);
			setTotalNotifications(response.total);
			setTotalPages(Math.ceil(response.total / itemsPerPage));
			// Initialize itemsPerPage from backend response if available
			if (response.limit && response.limit !== itemsPerPage) {
				setItemsPerPage(response.limit);
			}
		} catch (err) {
			const payload = getApiErrorPayload(err);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			setError(errorMessage || t("notifications.errors.fetchFailed"));
			console.error("Notifications fetch error:", err);
			toast({
				title: t("notifications.messages.fetchError"),
				description: errorMessage,
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}, [
		currentPage,
		itemsPerPage,
		selectedType,
		selectedStatus,
		selectedPriority,
		searchTerm,
		dateFrom,
		dateTo,
		user,
		t,
		toast,
	]);

	// Fetch users for notification creation
	useEffect(() => {
		const fetchUsers = async () => {
			try {
				const response = await usersAPI.getAll({ limit: 1000 });
				setUsers(response || []);
			} catch (err) {
				console.error("Failed to fetch users:", err);
			}
		};
		fetchUsers();
	}, []);

	// Refetch when filters change (reset to page 1)
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedType, selectedStatus, selectedPriority, dateFrom, dateTo]);

	// Debounce search term and reset to page 1
	useEffect(() => {
		const timer = setTimeout(() => {
			setCurrentPage(1);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Initial fetch and refetch when dependencies change
	useEffect(() => {
		fetchNotifications();
	}, [fetchNotifications]);

	const handleMarkAsRead = async (notificationId: string) => {
		try {
			await notificationsAPI.markAsRead(notificationId);

			// Update local state
			setNotifications(prev =>
				prev.map(notification =>
					notification.id === notificationId
						? { ...notification, status: "read", readAt: new Date().toISOString() }
						: notification,
				),
			);

			toast({
				title: t("notifications.messages.updateSuccess"),
				description: t("notifications.messages.updateSuccessDescription"),
			});
		} catch (err) {
			const payload = getApiErrorPayload(err);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("notifications.messages.updateError"),
				description: errorMessage || t("notifications.messages.updateErrorDescription"),
				variant: "destructive",
			});
		}
	};

	const handleDeleteNotification = async (notificationId: number | string) => {
		if (await confirmAction(t("notifications.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await notificationsAPI.delete(notificationId);

				// Update local state
				setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
				setTotalNotifications(prev => prev - 1);

				toast({
					title: t("notifications.messages.deleteSuccess"),
					description: t("notifications.messages.deleteSuccessDescription"),
				});
			} catch (err) {
				const payload = getApiErrorPayload(err);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("notifications.messages.deleteError"),
					description: errorMessage || t("notifications.messages.deleteErrorDescription"),
					variant: "destructive",
				});
			}
		}
	};

	const handleMarkAllAsRead = async () => {
		try {
			await notificationsAPI.markAllAsRead();

			// Update local state
			setNotifications(prev =>
				prev.map(notification => ({
					...notification,
					status: "read" as const,
					readAt: new Date().toISOString(),
				})),
			);

			toast({
				title: t("notifications.messages.updateSuccess"),
				description: t("notifications.messages.allMarkedAsRead"),
			});
		} catch (err) {
			const payload = getApiErrorPayload(err);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("notifications.messages.updateError"),
				description: errorMessage || t("notifications.errors.markAllAsReadFailed"),
				variant: "destructive",
			});
		}
	};

	// Validation function
	const validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		// Title validation
		if (!formData.title.trim()) {
			errors.title = t("notifications.validation.titleRequired");
		}

		// UserId validation
		if (!formData.userId) {
			errors.userId = t("notifications.validation.userIdRequired");
		} else {
			// Validate UUID format
			const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
			if (!uuidRegex.test(formData.userId)) {
				errors.userId = t("notifications.validation.userIdInvalid");
			}
		}

		// ActionUrl validation (if provided)
		if (formData.actionUrl && formData.actionUrl.trim()) {
			try {
				new URL(formData.actionUrl);
			} catch {
				errors.actionUrl = t("notifications.validation.actionUrlInvalid");
			}
		}

		// ExpiresAt validation (if provided)
		if (formData.expiresAt) {
			const expiresDate = new Date(formData.expiresAt);
			const now = new Date();
			if (isNaN(expiresDate.getTime())) {
				errors.expiresAt = t("notifications.validation.expiresAtInvalid");
			} else if (expiresDate <= now) {
				errors.expiresAt = t("notifications.validation.expiresAtFuture");
			}
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleCreateNotification = async () => {
		// Clear previous errors
		setFormErrors({});

		// Validate form
		if (!validateForm()) {
			toast({
				title: t("common.error"),
				description: t("notifications.validation.formInvalid"),
				variant: "destructive",
			});
			return;
		}

		try {
			setIsCreating(true);
			const payload: any = {
				title: formData.title.trim(),
				type: formData.type,
				priority: formData.priority,
				userId: formData.userId,
			};

			if (formData.message?.trim()) payload.message = formData.message.trim();
			if (formData.actionUrl?.trim()) payload.actionUrl = formData.actionUrl.trim();
			if (formData.actionText?.trim()) payload.actionText = formData.actionText.trim();
			if (formData.expiresAt) payload.expiresAt = new Date(formData.expiresAt).toISOString();

			await notificationsAPI.create(payload);

			toast({
				title: t("notifications.messages.updateSuccess"),
				description: t("notifications.messages.created"),
			});

			// Reset form and close dialog
			setFormData({
				title: "",
				message: "",
				type: "INFO",
				priority: "MEDIUM",
				userId: "",
				actionUrl: "",
				actionText: "",
				expiresAt: "",
			});
			setFormErrors({});
			setIsDialogOpen(false);

			// Refetch notifications
			fetchNotifications();
		} catch (err) {
			// Extract validation errors from backend
			const validationErrors = extractValidationErrors(err);

			// Map backend field names to frontend field names
			const mappedErrors: Record<string, string> = {};
			Object.keys(validationErrors).forEach(key => {
				if (key === "_general") {
					// Show general errors in toast
					toast({
						title: t("notifications.errors.createFailed"),
						description: validationErrors[key],
						variant: "destructive",
					});
				} else {
					// Map backend field names to frontend
					const frontendKey = key === "userId" ? "userId" : key;
					mappedErrors[frontendKey] = validationErrors[key];
				}
			});

			// Set field-level errors
			if (Object.keys(mappedErrors).length > 0) {
				setFormErrors(mappedErrors);
			}

			// Also show general error message
			const payload = getApiErrorPayload(err);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			if (!validationErrors._general) {
				toast({
					title: t("notifications.errors.createFailed"),
					description: errorMessage,
					variant: "destructive",
				});
			}
		} finally {
			setIsCreating(false);
		}
	};

	const clearFilters = () => {
		setSearchTerm("");
		setSelectedType("");
		setSelectedStatus("");
		setSelectedPriority("");
		setDateFrom("");
		setDateTo("");
		setCurrentPage(1);
	};

	const getTypeIcon = (type: string) => {
		const iconConfig = {
			info: <Info className="h-4 w-4 text-primary" />,
			success: <CheckCircle className="h-4 w-4 text-success" />,
			warning: <AlertTriangle className="h-4 w-4 text-warning" />,
			error: <AlertCircle className="h-4 w-4 text-destructive" />,
		};
		return iconConfig[type as keyof typeof iconConfig] || <Info className="h-4 w-4" />;
	};

	const getPriorityBadge = (priority: string) => {
		const priorityConfig = {
			low: {
				variant: "text-muted-foreground",
				text: t("notifications.priority.low"),
			},
			medium: {
				variant: "text-warning",
				text: t("notifications.priority.medium"),
			},
			high: {
				variant: "text-destructive",
				text: t("notifications.priority.high"),
			},
		};

		const config = priorityConfig[priority as keyof typeof priorityConfig];
		return <span className={`text-xs ${config.variant}`}>{config.text}</span>;
	};
	if (isLoading && currentPage === 1) {
		return <Loading />;
	}

	return (
		<div className={`space-y-6 p-6 ${isRTL ? "text-right" : "text-left"}`}>
			{/* Header */}
			<div className="flex flex-wrap gap-3 items-center justify-between">
				<div>
					<PageHeading className="mb-2 ">{t("notifications.page.title")}</PageHeading>
					<p className="text-muted-foreground">{t("notifications.page.description")}</p>
				</div>

				<div className="flex items-center gap-2">
					{notifications.some(n => n.status === "unread") && (
						<Button variant="outline" onClick={handleMarkAllAsRead} className="flex items-center gap-2">
							<CheckCircle2 className="h-4 w-4" />
							{t("notifications.actions.markAllAsRead")}
						</Button>
					)}
					<PermissionGate permission="notifications.create">
						<Button onClick={() => setIsDialogOpen(true)} className="flex items-center gap-2">
							<Plus className="h-4 w-4" />
							{t("notifications.newNotification")}
						</Button>
					</PermissionGate>
				</div>
			</div>

			{/* Filters and Search */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between py-2">
					<CardTitle className="flex items-center gap-2 text-base">
						<Filter className="h-5 w-5" />
						{t("notifications.searchAndFilters")}
					</CardTitle>
					<Button variant="link" className="flex h-12 w-20 text-destructive" onClick={clearFilters}>
						{t("notifications.clearFilters")}
					</Button>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
						<div className="relative flex h-12 w-full ">
							<Search
								className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`}
							/>
							<Input
								placeholder={t("notifications.searchPlaceholder")}
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className={isRTL ? "pr-10" : "pl-10"}
							/>
						</div>

						<select
							aria-label={t("notifications.allTypes")}
							value={selectedType}
							onChange={e => setSelectedType(e.target.value)}
							className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<option value="">{t("notifications.allTypes")}</option>
							<option value="info">{t("notifications.type.info")}</option>
							<option value="success">{t("notifications.type.success")}</option>
							<option value="warning">{t("notifications.type.warning")}</option>
							<option value="error">{t("notifications.type.error")}</option>
						</select>

						<select
							aria-label={t("notifications.allStatuses")}
							value={selectedStatus}
							onChange={e => setSelectedStatus(e.target.value)}
							className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<option value="">{t("notifications.allStatuses")}</option>
							<option value="unread">{t("notifications.status.unread")}</option>
							<option value="read">{t("notifications.status.read")}</option>
							<option value="archived">{t("notifications.status.archived")}</option>
						</select>

						<select
							aria-label={t("notifications.allPriorities")}
							value={selectedPriority}
							onChange={e => setSelectedPriority(e.target.value)}
							className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<option value="">{t("notifications.allPriorities")}</option>
							<option value="low">{t("notifications.priority.low")}</option>
							<option value="medium">{t("notifications.priority.medium")}</option>
							<option value="high">{t("notifications.priority.high")}</option>
						</select>

						<Input
							type="date"
							placeholder={t("notifications.fromDate")}
							aria-label={t("notifications.fromDate")}
							value={dateFrom}
							onChange={e => setDateFrom(e.target.value)}
							className="flex h-12 w-full"
						/>

						<Input
							type="date"
							placeholder={t("notifications.toDate")}
							aria-label={t("notifications.toDate")}
							value={dateTo}
							onChange={e => setDateTo(e.target.value)}
							className="flex h-12 w-full"
						/>
					</div>
				</CardContent>
			</Card>

			{/* Notifications Table */}
			<Card className="pt-2">
				<CardContent>
					{isLoading && currentPage > 1 ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
						</div>
					) : notifications.length === 0 ? (
						<div className="text-center py-12">
							<Bell className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
							<h3 className="text-lg font-semibold mb-2">{t("notifications.empty.title")}</h3>
							<p className="text-muted-foreground">{t("notifications.empty.description")}</p>
						</div>
					) : (
						<div className="record-surface">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("notifications.table.number")}</TableHead>
										<TableHead>{t("notifications.table.title")}</TableHead>
										<TableHead>{t("notifications.table.message")}</TableHead>
										<TableHead className="text-center">
											{t("notifications.table.recipient")}
										</TableHead>
										<TableHead className="text-center">
											{t("notifications.table.priority")}
										</TableHead>
										<TableHead className="text-center">{t("notifications.table.status")}</TableHead>
										<TableHead className="text-center">{t("notifications.table.sentAt")}</TableHead>
										<TableHead className="text-center">
											{t("notifications.table.actions")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{notifications.map((notification, index) => (
										<TableRow key={notification.id}>
											<TableCell>{notification.id}</TableCell>
											<TableCell className="font-medium">
												<div className="flex items-center gap-2">
													{getTypeIcon(notification.type)}
													<span className="truncate max-w-[200px]">{notification.title}</span>
												</div>
											</TableCell>
											<TableCell className="max-w-xs">
												<div className="truncate" title={notification.message}>
													{notification.message || "-"}
												</div>
											</TableCell>
											<TableCell className="text-center">{notification.recipient}</TableCell>
											<TableCell className="text-center">
												{getPriorityBadge(notification.priority)}
											</TableCell>
											<TableCell className="text-center">
												<span
													className={`text-xs ${notification.status === "unread" ? "text-warning" : notification.status === "archived" ? "text-muted-foreground" : "text-success"}`}
												>
													{notification.status === "unread"
														? t("notifications.status.unread")
														: notification.status === "archived"
															? t("notifications.status.archived")
															: t("notifications.status.read")}
												</span>
											</TableCell>
											<TableCell className="text-center text-muted-foreground text-sm">
												{formatDate(notification.sentAt)}
											</TableCell>
											<TableCell className="text-center">
												<div className="flex items-center justify-end gap-2">
													{notification.status === "unread" && (
														<Button
															aria-label={t("notifications.markAsRead")}
															variant="outline"
															size="sm"
															onClick={() => handleMarkAsRead(notification.id)}
															title={t("notifications.markAsRead")}
														>
															<CheckCircle className="h-4 w-4" />
														</Button>
													)}
													<PermissionGate permission="notifications.read">
														<Button
															aria-label={t("notifications.actions.view")}
															variant="outline"
															size="sm"
															onClick={() =>
																navigate(`/notifications/${notification.id}`)
															}
															title={t("notifications.actions.view")}
														>
															<Eye className="h-4 w-4 text-primary" />
														</Button>
													</PermissionGate>
													<PermissionGate permission="notifications.delete">
														<Button
															aria-label={t("common.delete")}
															variant="outline"
															size="sm"
															onClick={() => handleDeleteNotification(notification.id)}
															title={t("common.delete")}
														>
															<Trash2 className="h-4 w-4 text-destructive" />
														</Button>
													</PermissionGate>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Pagination */}
			<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalNotifications}
				itemsPerPage={itemsPerPage}
				onPageChange={setCurrentPage}
				onPageSizeChange={setItemsPerPage}
				itemName="notifications.notifications"
			/>

			{/* Error State */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center">
							<Bell className="h-12 w-12 text-destructive mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("notifications.errorLoading")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchNotifications}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* New Notification Dialog */}
			<Dialog
				open={isDialogOpen}
				onOpenChange={open => {
					setIsDialogOpen(open);
					if (!open) {
						setFormErrors({});
					}
				}}
			>
				<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{t("notifications.actions.create")}</DialogTitle>
						<DialogDescription>{t("notifications.dialog.createDescription")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="title">{t("notifications.fields.title")} *</Label>
							<Input
								id="title"
								placeholder={t("notifications.fields.titlePlaceholder")}
								value={formData.title}
								onChange={e => {
									setFormData({ ...formData, title: e.target.value });
									if (formErrors.title) {
										setFormErrors(prev => {
											const newErrors = { ...prev };
											delete newErrors.title;
											return newErrors;
										});
									}
								}}
								className={formErrors.title ? "border-destructive" : ""}
							/>
							{formErrors.title && <p className="text-sm text-destructive">{formErrors.title}</p>}
						</div>

						<div className="space-y-2">
							<Label htmlFor="message">{t("notifications.fields.message")}</Label>
							<Textarea
								id="message"
								placeholder={t("notifications.fields.messagePlaceholder")}
								value={formData.message}
								onChange={e => setFormData({ ...formData, message: e.target.value })}
								rows={4}
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="type">{t("notifications.fields.type")} *</Label>
								<Select
									value={formData.type}
									onValueChange={(value: any) => setFormData({ ...formData, type: value })}
								>
									<SelectTrigger id="type">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="INFO">{t("notifications.type.info")}</SelectItem>
										<SelectItem value="SUCCESS">{t("notifications.type.success")}</SelectItem>
										<SelectItem value="WARNING">{t("notifications.type.warning")}</SelectItem>
										<SelectItem value="ERROR">{t("notifications.type.error")}</SelectItem>
										<SelectItem value="SYSTEM">{t("notifications.type.system")}</SelectItem>
										<SelectItem value="BUSINESS">{t("notifications.type.business")}</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="priority">{t("notifications.fields.priority")} *</Label>
								<Select
									value={formData.priority}
									onValueChange={(value: any) => setFormData({ ...formData, priority: value })}
								>
									<SelectTrigger id="priority">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="LOW">{t("notifications.priority.low")}</SelectItem>
										<SelectItem value="MEDIUM">{t("notifications.priority.medium")}</SelectItem>
										<SelectItem value="HIGH">{t("notifications.priority.high")}</SelectItem>
										<SelectItem value="URGENT">{t("notifications.priority.urgent")}</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="userId">{t("notifications.fields.userId")} *</Label>
							<Select
								value={formData.userId}
								onValueChange={value => {
									setFormData({ ...formData, userId: value });
									if (formErrors.userId) {
										setFormErrors(prev => {
											const newErrors = { ...prev };
											delete newErrors.userId;
											return newErrors;
										});
									}
								}}
							>
								<SelectTrigger id="userId" className={formErrors.userId ? "border-destructive" : ""}>
									<SelectValue placeholder={t("notifications.fields.userIdPlaceholder")} />
								</SelectTrigger>
								<SelectContent>
									{users.map(user => (
										<SelectItem key={user.id} value={String(user.id)}>
											{user.firstName} {user.lastName} ({user.email})
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{formErrors.userId && <p className="text-sm text-destructive">{formErrors.userId}</p>}
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="actionUrl">{t("notifications.fields.actionUrl")}</Label>
								<Input
									id="actionUrl"
									placeholder={t("notifications.fields.actionUrlPlaceholder")}
									value={formData.actionUrl}
									onChange={e => {
										setFormData({ ...formData, actionUrl: e.target.value });
										if (formErrors.actionUrl) {
											setFormErrors(prev => {
												const newErrors = { ...prev };
												delete newErrors.actionUrl;
												return newErrors;
											});
										}
									}}
									className={formErrors.actionUrl ? "border-destructive" : ""}
								/>
								{formErrors.actionUrl && (
									<p className="text-sm text-destructive">{formErrors.actionUrl}</p>
								)}
							</div>

							<div className="space-y-2">
								<Label htmlFor="actionText">{t("notifications.fields.actionText")}</Label>
								<Input
									id="actionText"
									placeholder={t("notifications.fields.actionTextPlaceholder")}
									value={formData.actionText}
									onChange={e => setFormData({ ...formData, actionText: e.target.value })}
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="expiresAt">{t("notifications.fields.expiresAt")}</Label>
							<Input
								id="expiresAt"
								type="datetime-local"
								value={formData.expiresAt}
								onChange={e => {
									setFormData({ ...formData, expiresAt: e.target.value });
									if (formErrors.expiresAt) {
										setFormErrors(prev => {
											const newErrors = { ...prev };
											delete newErrors.expiresAt;
											return newErrors;
										});
									}
								}}
								className={formErrors.expiresAt ? "border-destructive" : ""}
							/>
							{formErrors.expiresAt && <p className="text-sm text-destructive">{formErrors.expiresAt}</p>}
						</div>
					</div>
					<DialogFooter className="w-full justify-between flex flex-col-reverse sm:flex-row sm:justify-between sm:space-x-2">
						<Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isCreating}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleCreateNotification} disabled={isCreating}>
							{isCreating ? t("common.creating") : t("notifications.actions.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default NotificationsPage;
